import { QueryFunctionContext, useQuery, useQueryClient } from 'react-query';
import healthcareProviderService from '../services/healthcareProvider.service';
import { ExtraFacilities, HealthcareProvider } from '@chiroup/core';
import { useContext } from 'react';
import { MeContext } from '../contexts/me.context';

const listQuery = (userId: string, clinicId = -1) => {
  return async (context: QueryFunctionContext) => {
    return healthcareProviderService.listAllFacilities(userId, clinicId);
  };
};

type FacilityOption = { text: string; value: number };

type Props = {
  refetchOnMount?: boolean;
};

const useFacilities = ({ refetchOnMount = true }: Props = {}) => {
  const meContext = useContext(MeContext);
  const queryClient = useQueryClient();
  const { data, isFetching, isSuccess } = useQuery<{
    data: HealthcareProvider[];
  }>(
    ['facilities'],
    listQuery(meContext?.me?.ID, meContext?.me.selectedClinic?.ID),
    {
      refetchOnWindowFocus: false,
      refetchOnMount,
    },
  );

  const transformedFacilities: FacilityOption[] =
    data?.data?.map((facility: HealthcareProvider) => ({
      text: facility?.facilityName ?? '',
      value: facility?.ID ?? -3,
    })) || [];

  transformedFacilities.unshift(
    { text: 'In-house', value: ExtraFacilities.InHouse },
    { text: 'Unspecified outside', value: ExtraFacilities.Unspecified },
  );
  const createProvider = async (provider: Partial<HealthcareProvider>) => {
    const res = await healthcareProviderService.create(
      provider,
      meContext.me.selectedClinic?.ID,
      meContext.me.ID,
    );

    if (provider?.facilityName) {
      queryClient.setQueryData(
        ['facilities'],
        (oldData: { data: HealthcareProvider[] } | undefined) => {
          if (!oldData) return { data: [res] };
          return {
            data: [...oldData.data, res],
          };
        },
      );
    }
  };

  return {
    data,
    isFetching,
    isSuccess,
    createProvider,
    facilityOptions: transformedFacilities,
  };
};

export default useFacilities;
